import { t } from 'i18next';

import {
  AgentMessageEventToolCallDataInput,
  AgentToolCallData,
  ELMA365FetchAppElementToolCallDataToolTypeEnum,
  ELMA365GeneralToolCallDataInput,
  ELMA365ToolkitToolCallData,
  ELMA365FetchCurrentUserToolCallDataToolTypeEnum,
  FileReadFileToolDataToolTypeEnum,
  FileToolCallDataInput,
  FileWriteFileToolDataToolTypeEnum,
  ToolsetSettingsType,
  FileDescribeImageToolDataToolTypeEnum,
  ELMA365FetchAppFeedToolCallDataToolTypeEnum,
  ELMA365ReadFileToolCallDataToolTypeEnum,
  ELMA365DescribeImageToolCallDataToolTypeEnum,
  SmartSearchToolCallData,
  SmartSearchDownloadDocumentToolCallDataToolTypeEnum,
  SmartSearchQueryToolCallDataToolTypeEnum,
  SmartSearchSearchDocumentsToolCallDataToolTypeEnum,
  ELMA365SearchTasksToolCallDataToolTypeEnum,
  ELMA365CreateTaskToolCallDataToolTypeEnum,
  ELMA365ReassignTaskToolCallDataToolTypeEnum,
  ELMA365UpdateTaskDatesToolCallDataToolTypeEnum,
  ELMA365UpdateTaskPercentToolCallDataToolTypeEnum,
  ELMA365FetchTaskToolCallDataToolTypeEnum,
  ELMA365FetchDocflowListsToolCallDataToolTypeEnum,
  SystemToolCallData,
  FinalAnswerToolDataToolTypeEnum,
} from '../../../../gamma-api';
import { IbIconName } from '../../../components/common/IbIcon';

export const getToolCallEventIconName = (data: AgentMessageEventToolCallDataInput): IbIconName => {
  switch (data.metadata.toolset_settings_type) {
    case ToolsetSettingsType.System:
      return 'system';
    case ToolsetSettingsType.McpServer:
      return 'file-code';
    case ToolsetSettingsType.SmartSearch:
      return 'optimize';
    case ToolsetSettingsType.DateTime:
      return 'time';
    case ToolsetSettingsType.Web:
      return 'link-two';
    case ToolsetSettingsType.Elma365General:
    case ToolsetSettingsType.Elma365Toolkit:
      return 'elma365';
    case ToolsetSettingsType.File:
      return 'file-text';
    case ToolsetSettingsType.Agent:
      return 'user-business';
    default:
      throw new Error(`Agent toolset type '${data.metadata.toolset_settings_type}' is not supported`);
  }
};

export const getToolCallEventName = (data: AgentMessageEventToolCallDataInput): string => {
  switch (data.metadata.toolset_settings_type) {
    case ToolsetSettingsType.System: {
      const toolCallData = data.metadata.data as SystemToolCallData;
      switch (toolCallData.tool_data.tool_type) {
        case FinalAnswerToolDataToolTypeEnum.FinalAnswer:
          return data.name;
        default:
          break;
      }
      break;
    }
    case ToolsetSettingsType.McpServer:
      return data.name;
    case ToolsetSettingsType.SmartSearch: {
      const toolCallData = data.metadata.data as SmartSearchToolCallData;
      switch (toolCallData.tool_data.tool_type) {
        case SmartSearchQueryToolCallDataToolTypeEnum.Query:
          return t('Agent: Tool call event: Smart search: Query', { project: toolCallData.project_name });
        case SmartSearchSearchDocumentsToolCallDataToolTypeEnum.SearchDocuments:
          return t('Agent: Tool call event: Smart search: Search documents', { project: toolCallData.project_name });
        case SmartSearchDownloadDocumentToolCallDataToolTypeEnum.DownloadDocument:
          return t('Agent: Tool call event: Smart search: Download document', { project: toolCallData.project_name });
        default:
          break;
      }
      break;
    }
    case ToolsetSettingsType.DateTime:
      return t('Agent: Tool call event: DateTime: Get current datetime');
    case ToolsetSettingsType.Web:
      return t('Agent: Tool call event: Web: Fetch URL');
    case ToolsetSettingsType.Elma365General: {
      const toolCallData = data.metadata.data as ELMA365GeneralToolCallDataInput;
      switch (toolCallData.tool_data.tool_type) {
        case ELMA365FetchAppElementToolCallDataToolTypeEnum.FetchAppElement:
          return t('Agent: Tool call event: ELMA365: Fetch app element');
        case ELMA365FetchAppFeedToolCallDataToolTypeEnum.FetchAppFeed:
          return t('Agent: Tool call event: ELMA365: Fetch app feed');
        case ELMA365ReadFileToolCallDataToolTypeEnum.ReadFile:
          return t('Agent: Tool call event: ELMA365: Read file');
        case ELMA365DescribeImageToolCallDataToolTypeEnum.DescribeImage:
          return t('Agent: Tool call event: ELMA365: Describe image');
        case ELMA365FetchCurrentUserToolCallDataToolTypeEnum.FetchCurrentUser:
          return t('Agent: Tool call event: ELMA365: Fetch current user');
        case ELMA365SearchTasksToolCallDataToolTypeEnum.SearchTasks:
          return t('Agent: Tool call event: ELMA365: Search tasks');
        case ELMA365FetchTaskToolCallDataToolTypeEnum.FetchTask:
          return t('Agent: Tool call event: ELMA365: Fetch task');
        case ELMA365CreateTaskToolCallDataToolTypeEnum.CreateTask:
          return t('Agent: Tool call event: ELMA365: Create task');
        case ELMA365ReassignTaskToolCallDataToolTypeEnum.ReassignTask:
          return t('Agent: Tool call event: ELMA365: Reassign task');
        case ELMA365UpdateTaskDatesToolCallDataToolTypeEnum.UpdateTaskDates:
          return t('Agent: Tool call event: ELMA365: Edit task dates');
        case ELMA365UpdateTaskPercentToolCallDataToolTypeEnum.UpdateTaskPercent:
          return t('Agent: Tool call event: ELMA365: Edit task completion percentage');
        case ELMA365FetchDocflowListsToolCallDataToolTypeEnum.FetchDocflowLists:
          return t('Agent: Tool call event: ELMA365: Fetch docflow lists');
        default:
          break;
      }
      break;
    }
    case ToolsetSettingsType.Elma365Toolkit: {
      const toolCallData = data.metadata.data as ELMA365ToolkitToolCallData;
      return toolCallData.tool_title;
    }
    case ToolsetSettingsType.File: {
      const toolCallData = data.metadata.data as FileToolCallDataInput;
      switch (toolCallData.tool_data.tool_type) {
        case FileReadFileToolDataToolTypeEnum.ReadFile:
          return t('Agent: Tool call event: File: Read file');
        case FileWriteFileToolDataToolTypeEnum.WriteFile:
          return t('Agent: Tool call event: File: Write file');
        case FileDescribeImageToolDataToolTypeEnum.DescribeImage:
          return t('Agent: Tool call event: File: Describe image');
        default:
          break;
      }
      break;
    }
    case ToolsetSettingsType.Agent: {
      const toolCallData = data.metadata.data as AgentToolCallData;
      return t('Agent: Tool call event: Agent: Call agent', { agentName: toolCallData.agent_name });
    }
  }
  return data.name;
};
